import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TIDS } from "@/constants/testIds";

/**
 * SignaturePad: tabs for Type vs Draw. Returns via onChange:
 *   { type: 'typed' | 'drawn', typedValue?: string, drawnDataUrl?: string }
 */
export default function SignaturePad({ initialTypedValue = "", onChange }) {
  const [tab, setTab] = useState("typed");
  const [typedValue, setTypedValue] = useState(initialTypedValue);
  const [hasDrawn, setHasDrawn] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    if (tab === "typed") {
      onChange && onChange({ type: "typed", typedValue: typedValue });
    } else if (tab === "drawn") {
      const dataUrl = hasDrawn && sigRef.current ? sigRef.current.toDataURL("image/png") : "";
      onChange && onChange({ type: "drawn", drawnDataUrl: dataUrl });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, typedValue, hasDrawn]);

  const handleClear = () => {
    if (sigRef.current) sigRef.current.clear();
    setHasDrawn(false);
    onChange && onChange({ type: "drawn", drawnDataUrl: "" });
  };

  const handleEnd = () => {
    setHasDrawn(true);
    if (sigRef.current) {
      const dataUrl = sigRef.current.toDataURL("image/png");
      onChange && onChange({ type: "drawn", drawnDataUrl: dataUrl });
    }
  };

  return (
    <div className="rrdc-card p-4">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 mb-4">
          <TabsTrigger value="typed" data-testid={TIDS.signTypeTab}>Type</TabsTrigger>
          <TabsTrigger value="drawn" data-testid={TIDS.signDrawTab}>Draw</TabsTrigger>
        </TabsList>
        <TabsContent value="typed">
          <label className="text-sm text-[#4B5563] mb-1 block">Type your full name to sign</label>
          <Input
            data-testid={TIDS.signTypedInput}
            value={typedValue}
            onChange={(e) => setTypedValue(e.target.value)}
            placeholder="Full name"
            className="h-12"
          />
          {typedValue ? (
            <div className="mt-3 px-4 py-3 border rounded-xl bg-[#F9FAFB]">
              <div className="text-xs text-[#6B7280] mb-1">Preview</div>
              <div style={{ fontFamily: "'Nunito', cursive", fontStyle: "italic", fontSize: 28, color: "#2F50A1" }}>
                {typedValue}
              </div>
            </div>
          ) : null}
        </TabsContent>
        <TabsContent value="drawn">
          <label className="text-sm text-[#4B5563] mb-1 block">Draw your signature below</label>
          <div className="relative">
            <SignatureCanvas
              ref={sigRef}
              penColor="#2F50A1"
              canvasProps={{
                className: "signature-canvas",
                "data-testid": TIDS.signCanvas,
              }}
              onEnd={handleEnd}
            />
          </div>
          <div className="mt-2">
            <Button type="button" variant="outline" onClick={handleClear} data-testid={TIDS.signClear}>
              Clear
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
